import DashboardGenComponent from "@/components/DashboardGenComponent.tsx";
import {useTranslation} from "react-i18next";
import st from "@/styles/Home.module.scss";
import CommonClass from "@/utils/common";
import clsx from "clsx";
import HomeDashboardCalculator from "@/components/HomeDashboardCalculator.tsx";
import { CircularProgress} from "@mui/material";
import CreditItem from "@/components/CreditItem.tsx";
import useAuth from "@/hooks/auth";
import Button from "@/ui/Button.tsx"
import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import {useAtom} from "jotai/index";
import {profile} from "@/atoms/profile.ts";
import {useNavigate} from "react-router-dom";
import DashboardClass from "@/utils/dashboard_class.ts";
import LazyImage from "@/ui/LazyImage.tsx";
import Images from "@/utils/images.ts";
import StatusMessage from "@/ui/StatusMessage.tsx";
import CalculatorIcon from "@/svg/CalculatorIcon.tsx";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import React, {useEffect} from "react";
import useLoans from "@/hooks/loans.ts";
import {pending_loans} from "@/atoms/common.ts";
import LoanApplicationTypesInterface from "@/types_and_interfaces/loan_types_interface.ts";
import Application from "@/components/Application.tsx";
import Factories from "@/utils/factories.ts";
import SignButKYCNotAllowedListener from "@/components/SignButKYCNotAllowedListener.tsx";
import CreditApplicationInterface from "@/types_and_interfaces/credit_application_interface.ts";
import RefreshButton from "@/components/RefreshButton.tsx";
import CreditLineNotDisbursedListItem from "@/components/CreditLineNotDisbursedListItem.tsx";
import {ApplicationsAndCrLineStatuses} from "@/types_and_interfaces/enums.ts";
import CreditLineTransactionList from "@/components/CreditLineTransactionsList.tsx";



const HomePage = () => {
    const {t} = useTranslation()
    const auth = useAuth()
    const loans = useLoans()
    const userCredits = loans.getStandardCredits()?.filter((x:any) => (x.r_status_simplified === "success" && x.r_status !== "settled"))

    // useEffect(() => {
    //     loans.updateUserLoans()
    // }, []);

    return (
        <>
            
            <DashboardGenComponent
                
                responsive1
                
                header_type={"primary"}
                
                heading={
                    <div className={st.heading}>
                        {t("welcome")}
                        
                        <span className={st.phone_number}>
                            {auth.getUserNaming()}
                        </span>
                    
                    </div>
                }
                
                header_content={
                    <div className={clsx(st.header_content)}>
                        
                        <Button light className={st.new_credit_btn} onClick={() => {
                            eventEmitter.emit(CommonClass.ModalEvents.OpenCalculator, true)
                        }}>
                            <CalculatorIcon/>
                            
                            {t("new_credit")}
                        
                        </Button>

                        <RefreshButton outlined />

                        <LazyImage
                            className={st.header_content_image}
                            src={Images.common.home_no_credit}
                            alt={"home_no_credit"}
                        />
                    </div>
                }
                
                
                column_center={
                    <ColumnCenter
                        userCredits={userCredits}
                        openCalculatorHandler={() => {
                            eventEmitter.emit(CommonClass.ModalEvents.OpenCalculator, true)
                        }}
                    />
                }
                
                column_right={
                    <div className={st.column_right}>
                        <HomeDashboardCalculator/>
                    </div>
                }
            />
        
        </>
    )
}

interface ColumnInterface {
    openCalculatorHandler(x:boolean):void
    userCredits:Array<LoanInterface> | undefined
}

const ColumnCenter:React.FC<ColumnInterface> = (props) => {
    
    const {
        userCredits,
    } = props
    
    const [applications] = useAtom(pending_loans);
    
    const {t} = useTranslation()
    const [user] = useAtom(profile)
    const navigate = useNavigate()
    const is_phone = Boolean(typeof user?.phone === "string" && user.phone.length)

    const loans = useLoans()
    const creditLinesNeedSign = loans.getLoansByType("Credit Line")?.filter((x) => ["approved_and_need_sign", "approved_and_not_verified"]?.includes(x.status))
    const creditLinesApproved = loans.getLoansByType("Credit Line")?.filter((x) => x.status === "approved")

    const typed_appl = applications as LoanApplicationTypesInterface
    const approvedAndNeedSign = applications  && typeof typed_appl.approved_and_need_sign !== 'undefined' ? typed_appl.approved_and_need_sign : [];
    const approvedAndNotVerified = applications  && typeof typed_appl.approved_and_not_verified !== 'undefined' ? typed_appl.approved_and_not_verified : [];
    // const pendings = applications  && typeof typed_appl.pending_approval !== 'undefined' ? typed_appl.pending_approval : [];
    let pendings: CreditApplicationInterface[] = []
    let approved: CreditApplicationInterface[] = []
    
    typed_appl && Object.keys(typed_appl).forEach((k: string) => {
        const st = Factories.application_status_simplification(k as ApplicationsAndCrLineStatuses);
        
        if (st === "pending" || st === "pending_needs_kyc") {
            const values = typed_appl[k as ApplicationsAndCrLineStatuses];
            
            if (Array.isArray(values)) {
                pendings = [...pendings, ...values];
            }
        }
        
        if(st === "approved_not_disbursed" || st === "approved") {
            const values = typed_appl[k as ApplicationsAndCrLineStatuses];
            
            if (Array.isArray(values)) {
                approved = [...approved, ...values];
            }
        }
    });

    useEffect(() => {
        if (user?.kyc_general_status === "ProcessingFailed") {
            navigate(DashboardClass.dashboard_routes.KYC)
        }
    }, [user?.kyc_general_status]);

    const restant = (Array.isArray(userCredits) && userCredits.length) ?
        userCredits.filter((x) => x.loan_age > 0).sort((a, b) => b.loan_age - a.loan_age) :
        []
    
    const non_restant = (Array.isArray(userCredits) && userCredits.length) ?
        userCredits.filter((x) => x.loan_age === 0).sort((a, b) => new Date(a.origin_created_at).getTime() - new Date(b.origin_created_at).getTime()) :
        []

    const user_again_kyc = user?.has_asp_kyc_conflict || user?.identity_card_is_expired;

    return (
        <div className={st.column_center}>
            
            <SignButKYCNotAllowedListener applications={approvedAndNeedSign} />



            {user_again_kyc ? (
                <div className={clsx(st.no_credits, st.error)}>
                    <strong>
                        {user?.has_asp_kyc_conflict ? t('has_asp_kyc_conflict')
                            : user?.identity_card_is_expired ? t('identity_card_is_expired')
                                : null}
                    </strong>

                    <Button
                        onClick={() => {
                            navigate(DashboardClass.dashboard_routes.KYC)
                        }}
                    >
                        {t("got_to_kyc")}
                    </Button>
                </div>
            ) :
                (user?.kyc_general_status !== "ProcessingSuccess" && (
                (
                    !Array.isArray(approvedAndNotVerified) ||
                    (Array.isArray(approvedAndNotVerified) && !approvedAndNotVerified.length)
                )
                &&
                (
                    !Array.isArray(approvedAndNeedSign) ||
                    (Array.isArray(approvedAndNeedSign) && !approvedAndNeedSign.length)
                )
                &&
                (
                    !Array.isArray(approved) ||
                    (Array.isArray(approved) && !approved.length)
                )

            )) ? (
                <>

                    {user?.kyc_general_status === "Unknown" && (

                        <div style={{gap:"4px"}} className={clsx(st.no_credits, st.status_0)}>
                            {(typeof typed_appl !== "undefined" && !Object.keys(typed_appl).length && !userCredits?.length) ? (
                                <strong>
                                    {t("see_credits")}
                                </strong>
                            ) : null}

                            {user?.permissions.is_kyc_allowed ? (
                                <>
                                    {(typeof typed_appl !== "undefined" &&  !Object.keys(typed_appl).length && !userCredits?.length) ? (
                                        <span className={st.sm_text}>
                                            {t("end_login")}
                                        </span>
                                    ) : null}


                                    <Button style={{marginTop:"16px"}} onClick={() => {
                                        navigate(DashboardClass.dashboard_routes.KYC)
                                    }}>
                                        {t("got_to_kyc")}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        disabled
                                        onClick={() => {
                                            navigate(DashboardClass.dashboard_routes.KYC)
                                        }}
                                    >
                                        {t("got_to_kyc")}
                                    </Button>

                                    <span className={clsx(st.sm_text, st.pending)}>
                                        {t("can_not_go_to_kyc")}
                                    </span>
                                </>
                            )}

                        </div>
                    )}

                    {user?.kyc_general_status === "UserStarted" && (
                        <div className={clsx(st.no_credits, st.status_1)}>

                            {user?.permissions.is_kyc_allowed ? (
                                <>
                                    <span>
                                        {t("continue_kyc")}
                                    </span>

                                    <Button onClick={() => {
                                        navigate(DashboardClass.dashboard_routes.KYC)
                                    }}>
                                        {t("continue")}
                                    </Button>
                                </>
                            ) : (
                                // <strong>
                                //     {t("can_not_go_to_kyc")}
                                // </strong>
                                <>

                                    <Button
                                        disabled
                                        onClick={() => {
                                            navigate(DashboardClass.dashboard_routes.KYC)
                                        }}
                                    >
                                        {t("got_to_kyc")}
                                    </Button>

                                    <span className={clsx(st.sm_text, st.pending)}>
                                        {t("can_not_go_to_kyc")}
                                    </span>
                                </>

                            )}


                        </div>
                    )}

                    {(user?.kyc_general_status === "UserFinished" || user?.kyc_general_status === "ServerProcessing") && (
                        <div className={clsx(st.no_credits)}>

                            <strong>
                                {t("processing_kyc")}
                            </strong>

                            {user?.permissions.is_kyc_allowed ? (
                                <Button
                                    onClick={() => {
                                        navigate(DashboardClass.dashboard_routes.KYC)
                                    }}
                                    disabled
                                >
                                    <CircularProgress color={"inherit"} size={24} />

                                    <span>
                                        {t("processing")}
                                    </span>
                                </Button>
                            ) : (
                                <>
                                    <Button
                                        disabled
                                        onClick={() => {
                                            navigate(DashboardClass.dashboard_routes.KYC)
                                        }}
                                    >
                                        {t("got_to_kyc")}
                                    </Button>

                                    <span className={clsx(st.sm_text, st.pending)}>
                                        {t("can_not_go_to_kyc")}
                                    </span>
                                </>

                            )}


                        </div>
                    )}

                    {user?.kyc_general_status === "ProcessingFailed" && (
                        <div className={clsx(st.no_credits, st.status_3)}>


                            <StatusMessage
                                is_phone={true}
                                status={"warning"}
                                text={t("failed_kyc")}
                                continue_kyc
                            />

                        </div>
                    )}

                    {user?.kyc_general_status === "ProcessingFailedAndSentToManager" && (
                        <div className={clsx(st.no_credits, st.status_5)}>

                            <StatusMessage
                                is_phone={is_phone}
                                status={"danger"}
                                text={t("failed_kyc1")}
                            />

                        </div>
                    )}

                </>
            ) : (
                <>
                    {(Array.isArray(userCredits) && !userCredits?.length) ? (

                        <div className={clsx(st.no_credits, st.status_2)}>

                            <StatusMessage
                                is_phone={true}
                                status={"success"}
                                text={t("success_kyc")}
                            />
                            {(typeof typed_appl !== "undefined" &&  !Object.keys(typed_appl).length && !userCredits?.length) ? (
                                <span>
                                    {t("no_credits")}
                                </span>
                            ) : null}

                        </div>

                    ) : null}
                </>
            )}
            
            {(Array.isArray(approvedAndNeedSign) && !!approvedAndNeedSign.length) ? (

                <>
                    
                    <h3>
                        {t("to_sign_appls")}
                    </h3>
                    
                    {approvedAndNeedSign?.map((app, i:number) => {
                        
                        return (
                            <Application
                                order={i}
                                simplified_status={
                                    Factories.application_status_simplification(app.status)
                                }
                                application={app}
                                key={`application_${app.id}`}
                            />
                        )
                    })}
                </>
            
            ) : null}
            
            {(Array.isArray(approvedAndNotVerified) && approvedAndNotVerified.length) ? (
                
                <>
                    
                    <h3>
                        {t("to_sign_appls")}
                    </h3>
                    
                    {approvedAndNotVerified?.map((app, i:number) => {
                        
                        return (
                            <Application
                                order={i}
                                simplified_status={
                                    Factories.application_status_simplification(app.status)
                                }
                                application={app}
                                key={`application_${app.id}`}
                            />
                        )
                    })}
                </>
            
            ) : null}
            
            {(Array.isArray(approved) && approved.length) ? (
                
                <>
                    
                    <h3>
                        {t("approved_loans")}
                    </h3>
                    
                    {approved?.map((app, i:number) => {
                        
                        return (
                            <Application
                                order={i}
                                simplified_status={
                                    Factories.application_status_simplification(app.status)
                                }
                                application={app}
                                key={`application_${app.id}`}
                            />
                        )
                    })}
                </>
            
            ) : null}
            
            {(Array.isArray(pendings) && pendings.length) ? (
                
                <>
                    
                    <h3>
                        {t("pending_credit1")}
                    </h3>
                    
                    {pendings?.map((app, i) => {
                        
                        return (
                            <Application
                                order={i}
                                simplified_status={
                                    Factories.application_status_simplification(app.status)
                                }
                                application={app}
                                key={`application_${app.id}`}
                            />
                        )
                    })}
                </>
            
            ) : null}
            
            {Array.isArray(userCredits) ? (
                <>
                    
                    {(Array.isArray(restant) && restant.length) ? (
                        
                        <>
                            
                            <h3>
                                {t("restants")}
                            </h3>
                            
                            {restant?.map((obj, i) => {
                                
                                return (
                                    <CreditItem obj={obj} key={`restant_credit_item_${i}`}/>
                                )
                            })}
                        </>
                    
                    ) : null}
                    
                    
                    
                    {non_restant.length ? (
                        <h3>
                            {t("active_loans")}
                        </h3>
                    ) : null}
                    
                    {userCredits.length ? non_restant.map((obj: LoanInterface, i) => {
                        return <CreditItem obj={obj} key={`credit_item_${i}`}/>
                    }) : null}
                    
                    {(!userCredits.length && (
                        !Array.isArray(approvedAndNeedSign) ||
                        !Array.isArray(approvedAndNotVerified)||
                        (Array.isArray(approvedAndNeedSign) && !approvedAndNeedSign.length) ||
                        (Array.isArray(approvedAndNotVerified) && !approvedAndNotVerified.length) ||
                        !Array.isArray(approved) ||
                        (Array.isArray(approved) && !approved.length) ||
                        !Array.isArray(pendings) ||
                        (Array.isArray(pendings) && !pendings.length)
                    )) ? (
                        <>
                            {user?.auth_type === "ByMPass" ? (
                                
                                <div className={clsx(st.no_credits, st.status_2)}>
                                    
                                    <StatusMessage
                                        is_phone={true}
                                        status={"success"}
                                        text={t("success_kyc")}
                                    />

                                    {(typeof typed_appl !== "undefined" &&  !Object.keys(typed_appl).length && !userCredits?.length) ? (
                                        <span>
                                            {t("no_credits")}
                                        </span>
                                    ) : null}
                                
                                </div>
                            
                            ) : (
                                (
                                    !Array.isArray(approvedAndNotVerified) ||
                                    (Array.isArray(approvedAndNotVerified) && !approvedAndNotVerified.length)
                                )
                                &&
                                (
                                    !Array.isArray(approvedAndNeedSign) ||
                                    (Array.isArray(approvedAndNeedSign) && !approvedAndNeedSign.length)
                                )
                                &&
                                (
                                    !Array.isArray(approved) ||
                                    (Array.isArray(approved) && !approved.length)
                                )
                                &&
                                (
                                    !Array.isArray(pendings) ||
                                    (Array.isArray(pendings) && !pendings.length)
                                )
                            ) ? (
                                <></>
                            ) : null}
                        
                        </>
                    ) : null}
                
                </>
            ) : (
                <CircularProgress
                    color={"success"}
                    size={60}
                    sx={{
                        display: "block",
                        margin: "0 auto",
                    }}
                />
            )}

            {Array.isArray(creditLinesNeedSign) &&
                !!creditLinesNeedSign?.length && (
                    <>
                        <h3>{t("line_credit_title_approved_and_need_sign")}</h3>
                        {creditLinesNeedSign?.map((credit_line: LoanInterface, i) => {
                                return (
                                    <CreditLineNotDisbursedListItem
                                        key={`credit_line_not_disbursed_list_item_${i}`}
                                        obj={credit_line as LoanInterface}
                                    />
                                )
                            }
                        )}
                    </>
                )}

            {Array.isArray(creditLinesApproved) &&
                !!creditLinesApproved?.length && (
                    <>
                        {creditLinesApproved?.map((credit_line: LoanInterface, i) => {

                                return credit_line?.transactions !== null && (
                                    <CreditLineTransactionList
                                        is_main_page
                                        key={`credit_line_${i}`}
                                        cr_line={credit_line as LoanInterface}
                                    />
                                )
                            }
                        )}
                    </>
                )}
        </div>
    )
}


export default HomePage